import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  Archive,
  ArrowRight,
  CalendarDays,
  CalendarRange,
  Check,
  ChevronRight,
  Clock3,
  Dumbbell,
  Home,
  Hourglass,
  Plus,
  Timer,
  UtensilsCrossed,
  Weight,
  type LucideIcon,
} from 'lucide-react';
import { Banner } from '@/components/Banner';
import { BotonEnlace, Button } from '@/components/Button';
import { Fila, Filas } from '@/components/Fila';
import { Emblema, Pastilla } from '@/components/Card';
import { PageHeader } from '@/components/PageHeader';
import { PastillaDeEtapa } from '@/components/PastillaDeEtapa';
import { ListaComidas } from '@/components/ListaComidas';
import { hayDatosCapturados, leerSettings } from '@/db/schema';
import { leerDatosDeHoy } from '@/db/hoy';
import { leerPesoDeHoy, type PesoDeHoy } from '@/db/pesajes';
import { avisoEtapaDeHoy, leerMetasDelDia, marcarAvisoEtapaVisto, type MetasYEtapa } from '@/db/metas';
import { leerAyunoDelDia, type AyunoDelDia } from '@/db/ayunos';
import { iniciarEntrenamiento } from '@/db/entrenamientos';
import { mensajeDeError } from '@/db/tx';
import type { MealEntry, Modalidad, Settings } from '@/db/types';
import { textoEnCurso, textoHoras, textoVentana } from '@/domain/ayuno';
import { formatearHoraDeInstante } from '@/domain/time';
import {
  AccionesDeAyuno,
  AvisoAyunoLargo,
  CerrarVentana,
  ControlesDelTemporizador,
  DetalleDeAyuno,
} from '@/features/food/AyunoPages';
import { evaluarRespaldo, textoUltimoRespaldo } from '@/domain/backup-reminder';
import {
  actividadDelDia,
  fechaCorta,
  fechaDelDia,
  resumirComidas,
  sesionDelDia,
  textoColaciones,
  textoEjercicios,
  type ActividadDelDia,
  type ResumenComidas,
  type SesionDelDia,
} from '@/domain/hoy';
import {
  NOMBRE_ETAPA,
  PLAN_DE_JANETH,
  esPerfilDeJaneth,
  metasEnPalabras,
  type AvisoEtapaNueva,
  type MetasDelDia,
} from '@/domain/etapas';
import { contarPlanes } from '@/db/planes';
import { ETIQUETAS_MODALIDAD } from '@/domain/modalidad';
import { avance, formatearGramos, formatearKcal, sumar } from '@/domain/nutricion';
import { formatearNumero } from '@/domain/numbers';
import { formatearCorto, formatearCortoConAnio, formatearMedio, nombreDia, type FechaLocal } from '@/domain/dates';
import { formatearCambio } from '@/domain/tendencia';
import { formatearPeso, type UnidadPeso } from '@/domain/units';
import { areaDe, disponible } from '@/app/areas';
import { useFechaHoy } from '@/lib/useFechaHoy';
import { Barra } from '@/features/food/comunes';
import { textoAvance } from '@/features/food/ResumenDelDia';

const FASE_GYM = areaDe('/gym')?.fase ?? 2;
const GYM_DISPONIBLE = disponible({ fase: FASE_GYM });

function lista(palabras: readonly string[]): string {
  if (palabras.length <= 1) return palabras.join('');
  return `${palabras.slice(0, -1).join(', ')} y ${palabras[palabras.length - 1]}`;
}

/**
 * «Tu día» (diseño Foco; Req. 7.3, 24): la fecha real, el aviso de etapa
 * nueva, la alimentación del día contra las metas, la sesión de
 * entrenamiento que corresponde, el resumen de desayuno, comida y cena, el
 * ayuno, el peso y el estado del respaldo.
 *
 * Todo sale de los registros reales y de la fecha local. Lo que todavía no
 * existe lo dice, con la fase en que llega, en lugar de mostrar datos de
 * ejemplo. Los datos del perfil para cálculos ya no se piden aquí: se piden
 * donde se usan (formulario depurado, 0.3.6).
 *
 * Reacomodo de la 0.11.0: encabezado compacto con el saludo y la etapa en una
 * pastilla, cada bloque en su tarjeta y el ayuno y el peso en un par de
 * tarjetas angostas. Nada se quitó: el detalle del ayuno se despliega y el
 * del plan vive en Mi plan.
 */
export function TodayPage() {
  const fecha = useFechaHoy();
  // El ayuno en curso avanza solo: se vuelve a calcular cada minuto (Req. 23.3)
  const ahora = useAhora(60_000);
  const settings = useLiveQuery(() => leerSettings(), []);
  const datos = useLiveQuery(() => leerDatosDeHoy(fecha), [fecha]);
  const metas = useLiveQuery(() => leerMetasDelDia(fecha), [fecha]);
  const hayDatos = useLiveQuery(() => hayDatosCapturados(), []);
  const planes = useLiveQuery(() => contarPlanes(), []);
  const peso = useLiveQuery(() => leerPesoDeHoy(fecha), [fecha]);

  if (
    settings === undefined ||
    datos === undefined ||
    metas === undefined ||
    hayDatos === undefined ||
    planes === undefined ||
    peso === undefined
  ) {
    return null;
  }

  const sesion = sesionDelDia(fecha, datos.enCurso, datos.ultima, GYM_DISPONIBLE ? datos.proxima : null);
  const comidas = resumirComidas(datos.comidas);
  const actividad = actividadDelDia(sesion, GYM_DISPONIBLE, FASE_GYM);
  const nombre = settings.nombre?.trim() ?? '';
  const avisoEtapa = avisoEtapaDeHoy(metas, settings, fecha);
  const ofrecerPlan = planes === 0 && esPerfilDeJaneth(settings.nombre);

  return (
    <div>
      <PageHeader
        compacto
        antetitulo={fechaDelDia(fecha)}
        titulo={nombre === '' ? 'Tu día' : `Hola, ${nombre}`}
        {...(metas.posicion !== null ? { extra: <PastillaDeEtapa posicion={metas.posicion} /> } : {})}
      />

      <div className="space-y-3">
        {avisoEtapa !== null && <AvisoDeEtapa key={avisoEtapa.clave} aviso={avisoEtapa} metas={metas.metas} />}

        {ofrecerPlan && (
          <Filas tarjetas>
            <Fila
              tarjeta
              icono={CalendarRange}
              titulo="Tu plan 6 + 2 está listo para crearlo"
              detalle={`Con tus metas confirmadas, desde el ${formatearCortoConAnio(PLAN_DE_JANETH.fechaInicio)}. Se crea solo si lo confirmas.`}
              a={`/mas/plan/nuevo?plantilla=${PLAN_DE_JANETH.plantilla}`}
            />
          </Filas>
        )}

        <TarjetaAlimentacion entradas={datos.comidas} metas={metas} />
        <TarjetaSesion sesion={sesion} actividad={actividad} />
        <TarjetaComidas fecha={fecha} comidas={comidas} />
        <ParAyunoYPeso fecha={fecha} ahora={ahora} peso={peso} unidad={settings.unidadPeso} />
        <Pendientes settings={settings} hayDatos={hayDatos} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** La hora de ahora, al minuto, para lo que avanza solo. */
function useAhora(cadaMs: number): Date {
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setAhora(new Date()), cadaMs);
    return () => window.clearInterval(t);
  }, [cadaMs]);
  return ahora;
}

/** Tarjeta de Foco con el título de una sección adentro. */
function Tarjeta({
  titulo,
  extra,
  children,
  className = '',
}: {
  titulo: string;
  extra?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section aria-label={titulo} className={`rounded-panel bg-superficie p-5 ${className}`.trim()}>
      <div className="mb-3 flex items-center justify-between gap-2.5">
        <h2 className="text-[17px] tracking-[-0.25px] text-texto">{titulo}</h2>
        {extra}
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Alimentación
// ---------------------------------------------------------------------------

interface MetaDeHoy {
  nombre: string;
  /** Nombre completo cuando el visible va recortado ("Carbos"). */
  nombreLargo?: string;
  consumido: number;
  meta: number | null;
  unidad: 'kcal' | 'g';
  /** Algún registro del día no trae este dato: el total puede ser mayor. */
  incompleta: boolean;
}

const conUnidad = (n: number, unidad: MetaDeHoy['unidad']) =>
  unidad === 'kcal' ? formatearKcal(n) : formatearGramos(n);

/** Para lectores de pantalla: "Proteína (incompleto): 98 g de 140 g, faltan 42 g". */
function etiquetaDeMeta(m: MetaDeHoy): string {
  const meta = m.meta === null ? '' : ` de ${conUnidad(m.meta, m.unidad)}`;
  const avanceTexto = textoAvance(m.consumido, m.meta, m.unidad);
  return `${m.nombreLargo ?? m.nombre}${m.incompleta ? ' (incompleto)' : ''}: ${conUnidad(m.consumido, m.unidad)}${meta}, ${avanceTexto}`;
}

/** Una columna de macro: el nombre, lo consumido contra la meta y su barra. */
function ColumnaMacro({ m }: { m: MetaDeHoy }) {
  const a = avance(m.consumido, m.meta);
  return (
    <div className="min-w-0 space-y-1.5">
      <p className="text-[12px] text-texto-suave">
        {m.nombreLargo === undefined ? (
          m.nombre
        ) : (
          <>
            <span aria-hidden="true">{m.nombre}</span>
            <span className="sr-only">{m.nombreLargo}</span>
          </>
        )}
      </p>
      <p className="text-[15px] font-medium tabular-nums text-texto">
        {formatearNumero(m.consumido, { decimales: 1, fijos: false })}
        {m.incompleta && <span className="text-aviso">*</span>}
        {m.meta !== null && (
          <span className="text-[12px] font-normal text-texto-suave"> / {conUnidad(m.meta, m.unidad)}</span>
        )}
      </p>
      <Barra proporcion={a.proporcion} />
      {/* Un número suelto se explica: sin meta no hay contra qué comparar, y un
          registro sin ese macro deja el total corto (Req. 24.3) */}
      {m.meta === null ? (
        <p className="text-[11px] text-texto-suave">Sin meta</p>
      ) : (
        m.incompleta && <p className="text-[11px] text-aviso">Datos incompletos</p>
      )}
    </div>
  );
}

/** "Te quedan 1,036 kcal", "120 kcal arriba de la meta", "Falta tu mantenimiento". */
function restanteDeKcal(
  consumido: number,
  meta: number | null,
  faltaMantenimiento: boolean,
): { etiqueta: string; valor: string } {
  if (faltaMantenimiento) return { etiqueta: 'Tu meta', valor: 'Falta tu mantenimiento' };
  const a = avance(consumido, meta);
  if (a.meta === null) return { etiqueta: 'Tu meta', valor: 'Sin definir' };
  if (a.faltan !== null) return { etiqueta: 'Te quedan', valor: formatearKcal(a.faltan) };
  if (a.exceso !== null) return { etiqueta: 'Arriba de la meta', valor: formatearKcal(a.exceso) };
  return { etiqueta: 'Tu meta', valor: 'Cumplida' };
}

/**
 * Las metas del día contra lo registrado, con lo que falta (Req. 18.10,
 * 24.1): las kcal grandes con su barra y lo que queda, y los tres macros en
 * columnas. Carbohidratos y grasa solo cuando tienen meta (una meta que no
 * está definida no se muestra). Un registro sin un macro deja ese total
 * marcado como incompleto, como en Comida.
 */
function TarjetaAlimentacion({ entradas, metas }: { entradas: readonly MealEntry[]; metas: MetasYEtapa }) {
  const totales = sumar(entradas);
  const m = metas.metas;
  const kcal = avance(totales.kcal, m.kcal);
  const restante = restanteDeKcal(totales.kcal, m.kcal, m.faltaMantenimiento);
  const macros: MetaDeHoy[] = [
    {
      nombre: 'Proteína',
      consumido: totales.proteinaG,
      meta: m.proteinaG,
      unidad: 'g' as const,
      incompleta: totales.sinDato.proteinaG > 0,
    },
    {
      nombre: 'Carbos',
      nombreLargo: 'Carbohidratos',
      consumido: totales.carbohidratosG,
      meta: m.carbohidratosG,
      unidad: 'g' as const,
      incompleta: totales.sinDato.carbohidratosG > 0,
    },
    {
      nombre: 'Grasa',
      consumido: totales.grasaG,
      meta: m.grasaG,
      unidad: 'g' as const,
      incompleta: totales.sinDato.grasaG > 0,
    },
  ].filter((x) => x.nombre === 'Proteína' || x.meta !== null);
  const kcalComoMeta: MetaDeHoy = {
    nombre: 'Calorías',
    consumido: totales.kcal,
    meta: m.kcal,
    unidad: 'kcal',
    incompleta: false,
  };

  return (
    <Link
      to="/comida"
      aria-label={`Tu alimentación de hoy. ${[kcalComoMeta, ...macros].map(etiquetaDeMeta).join('. ')}. Ver Comida`}
      className="block rounded-panel bg-superficie p-5 active:opacity-70"
    >
      <div className="flex items-center justify-between gap-2.5">
        <h2 className="text-[17px] tracking-[-0.25px] text-texto">Tu alimentación</h2>
        <Pastilla>Hoy</Pastilla>
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        <p className="tabular-nums text-texto">
          {/* kcal sin decimales y macros con uno (design.md, 4.2) */}
          <span className="text-[34px] leading-none tracking-[-1.2px]">
            {formatearNumero(totales.kcal, { decimales: 0 })}
          </span>
          <span className="text-[14px] text-texto-suave">
            {m.kcal === null ? ' kcal' : ` / ${formatearKcal(m.kcal)}`}
          </span>
        </p>
        <p className="shrink-0 text-right text-[11px] leading-tight text-texto-suave">
          {restante.etiqueta}
          <br />
          <span className="text-[13px] font-medium tabular-nums text-texto">{restante.valor}</span>
        </p>
      </div>

      <div className="mt-3">
        <Barra proporcion={kcal.proporcion} pasada={kcal.exceso !== null} />
      </div>

      <div
        className={`mt-4 grid gap-3 border-t border-borde pt-4 ${macros.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}
      >
        {macros.map((x) => (
          <ColumnaMacro key={x.nombre} m={x} />
        ))}
      </div>

      {/* De dónde salen los macros de hoy, si no los puso la etapa (0.5.1) */}
      {m.macrosDeMantenimiento && (
        <p className="mt-3 text-[12px] leading-relaxed text-texto-suave">
          Los macros de hoy salen de tu mantenimiento.
        </p>
      )}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Ayuno y peso, en un par de tarjetas angostas
// ---------------------------------------------------------------------------

/** Tarjeta angosta: emblema, etiqueta, el dato grande, su detalle y un acceso. */
function TarjetaAngosta({
  icono: Icono,
  etiqueta,
  valor,
  detalle,
  nota,
  esquina,
  children,
}: {
  icono: LucideIcon;
  etiqueta: string;
  valor: string;
  detalle: string;
  nota?: string;
  /** Acceso pequeño en la esquina, como el "+" de registrar un peso. */
  esquina?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-2.5 rounded-panel bg-superficie p-4">
      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-superficie-suave text-marca">
        <Icono aria-hidden="true" className="size-[18px]" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-1.5">
          <p className="text-[12px] text-texto-suave">{etiqueta}</p>
          {esquina}
        </div>
        <p className="mt-0.5 text-[17px] leading-tight tabular-nums text-texto">{valor}</p>
        <p className="mt-0.5 text-[11px] leading-snug text-texto-suave">{detalle}</p>
        {nota !== undefined && <p className="text-[11px] leading-snug text-texto-suave">{nota}</p>}
        <div className="mt-1.5">{children}</div>
      </div>
    </div>
  );
}

/** Enlace corto del pie de una tarjeta angosta: "Ver progreso ›". */
function AccesoCorto({ a, children }: { a: string; children: ReactNode }) {
  return (
    <Link to={a} className="-ml-1 inline-flex min-h-11 items-center gap-0.5 px-1 text-[12px] font-medium text-marca active:opacity-60">
      {children}
      <ChevronRight aria-hidden="true" className="size-3.5" />
    </Link>
  );
}

/**
 * ¿El ayuno del día pide algo? Un aviso de más de 24 h, el temporizador que
 * ella empieza y termina, uno por confirmar, un cambio por resolver o datos
 * que faltan. En esos casos el detalle no se esconde detrás de un toque; con
 * el ayuno ya resuelto, la tarjeta se queda con el resumen.
 */
function pideAtencion(datos: AyunoDelDia): boolean {
  if (datos.avisoLargo !== null || datos.metodo === 'temporizador') return true;
  return datos.estado.tipo !== 'registrado' && datos.estado.tipo !== 'sin_ayuno';
}

/**
 * Lo corto que cabe en la tarjeta; el detalle completo se despliega.
 *
 * La etiqueta cambia con el estado, porque son dos cosas distintas: "Ayuno"
 * mientras se ayuna y "Ventana de alimentación" mientras se come (Req. 23.14).
 */
function resumenCortoDeAyuno(datos: AyunoDelDia): { etiqueta: string; valor: string; detalle: string } {
  if (datos.ventana !== null) {
    return {
      etiqueta: 'Ventana de alimentación',
      valor: textoHoras(datos.ventana.horas),
      detalle: `Desde las ${formatearHoraDeInstante(datos.ventana.desde)}`,
    };
  }
  if (datos.enCurso !== null) {
    return {
      etiqueta: 'En ayuno',
      valor: textoHoras(datos.enCurso.horas),
      detalle: `Desde las ${formatearHoraDeInstante(datos.enCurso.inicio)}`,
    };
  }
  const e = datos.estado;
  const ayuno = (valor: string, detalle: string) => ({ etiqueta: 'Ayuno', valor, detalle });
  switch (e.tipo) {
    case 'registrado':
      return ayuno(textoHoras(e.horas), 'Registrado');
    case 'por_confirmar':
      return ayuno(textoHoras(e.horas), 'Aún no cuenta');
    case 'cambio':
      return ayuno(textoHoras(e.horasConfirmadas), 'Hay un cambio por revisar');
    case 'datos_insuficientes':
      return ayuno('Faltan datos', 'Sin registros para calcularlo');
    case 'sin_ayuno':
      return ayuno('Sin ayuno', 'Hoy no termina ninguno');
  }
}

/**
 * El ayuno y el peso de hoy, uno junto al otro (Req. 23.2, 23.3, 24.1).
 *
 * La tarjeta del ayuno dice lo de un vistazo y "Ver detalle" despliega todo
 * lo que había antes: el aviso de más de 24 h, el temporizador, el estado del
 * día y sus acciones. Se abre sola cuando hay algo que atender, para que una
 * alerta o el botón del temporizador no queden escondidos.
 */
function ParAyunoYPeso({
  fecha,
  ahora,
  peso,
  unidad,
}: {
  fecha: FechaLocal;
  ahora: Date;
  peso: PesoDeHoy;
  unidad: UnidadPeso;
}) {
  // El minuto, no el instante: así la consulta no se repite a cada render
  const minuto = Math.floor(ahora.getTime() / 60_000);
  const datos = useLiveQuery(() => leerAyunoDelDia(fecha, new Date(minuto * 60_000)), [fecha, minuto]);
  const [abierto, setAbierto] = useState(false);
  // Una vez abierto por algo que atender, se queda abierto: después de
  // confirmar un ayuno se sigue viendo en qué quedó, sin volver a tocar
  const hayQueAtender = datos !== undefined && pideAtencion(datos);
  useEffect(() => {
    if (hayQueAtender) setAbierto(true);
  }, [hayQueAtender]);
  if (datos === undefined) return null;

  const { estado, enCurso, metaHoras } = datos;
  const corto = resumenCortoDeAyuno(datos);
  const verDetalle = abierto || pideAtencion(datos);

  const { resumen, basculaDeTendencia } = peso;
  const ultimo = resumen.ultimo;
  // Si la tendencia cuenta solo una báscula, Hoy lo dice como Cuerpo (revisión de la 0.4.0)
  const soloUna = basculaDeTendencia === null ? null : basculaDeTendencia.nombre;
  const notaPeso =
    ultimo === null
      ? null
      : resumen.tendenciaKg === null
        ? soloUna === null
          ? 'Sin pesajes en los últimos 7 días para la tendencia.'
          : `Sin pesajes de ${soloUna} en los últimos 7 días: cuenta solo esa báscula.`
        : `Tendencia ${formatearPeso(resumen.tendenciaKg, unidad)}${
            resumen.cambioSemanalKg === null ? '' : ` · ${formatearCambio(resumen.cambioSemanalKg, unidad)} en 7 días`
          }${soloUna === null ? '' : ` · cuenta solo ${soloUna}`}`;

  return (
    <>
      <div className="grid grid-cols-2 gap-2.5">
        <TarjetaAngosta
          icono={datos.ventana !== null ? UtensilsCrossed : Hourglass}
          etiqueta={corto.etiqueta}
          valor={corto.valor}
          detalle={corto.detalle}
          {...(metaHoras !== null
            ? { nota: `Meta: ${formatearNumero(metaHoras, { decimales: 1, fijos: false })} h` }
            : {})}
        >
          <button
            type="button"
            aria-expanded={verDetalle}
            onClick={() => setAbierto((v) => !v)}
            className="-ml-1 inline-flex min-h-11 items-center gap-0.5 px-1 text-[12px] font-medium text-marca"
          >
            {verDetalle ? 'Ocultar detalle' : 'Ver detalle'}
            <ChevronRight aria-hidden="true" className={`size-3.5 ${verDetalle ? 'rotate-90' : ''}`} />
          </button>
        </TarjetaAngosta>

        <TarjetaAngosta
          icono={Weight}
          etiqueta="Peso"
          valor={ultimo === null ? 'Sin pesajes' : formatearPeso(ultimo.pesoKg, unidad)}
          detalle={ultimo === null ? 'Registra tu peso para ver tu tendencia de 7 días.' : `Último · ${fechaCorta(ultimo.fecha)}`}
          {...(notaPeso === null ? {} : { nota: notaPeso })}
          {...(ultimo === null
            ? {}
            : {
                // Con pesajes, registrar sigue a un toque (Req. 24.2) y el pie lleva a la tendencia
                esquina: (
                  <Link
                    to="/cuerpo/pesaje/nuevo?desde=hoy"
                    aria-label="Registrar peso"
                    className="-mr-1 -mt-1 grid size-8 shrink-0 place-items-center rounded-full bg-marca-suave text-marca active:opacity-60"
                  >
                    <Plus aria-hidden="true" className="size-4" />
                  </Link>
                ),
              })}
        >
          {ultimo === null ? (
            <AccesoCorto a="/cuerpo/pesaje/nuevo?desde=hoy">Registrar peso</AccesoCorto>
          ) : (
            <AccesoCorto a="/cuerpo">Ver progreso</AccesoCorto>
          )}
        </TarjetaAngosta>
      </div>

      {verDetalle && (
        <section aria-label="Detalle de tu ayuno" className="space-y-2 rounded-panel bg-superficie p-4">
          {datos.avisoLargo !== null && <AvisoAyunoLargo aviso={datos.avisoLargo} />}
          {datos.ventana !== null && (
            <p className="flex items-start gap-[9px] text-[13px] leading-relaxed text-texto">
              <UtensilsCrossed className="mt-px size-4 shrink-0 text-marca" />
              <span>{textoVentana(datos.ventana)}</span>
            </p>
          )}
          {enCurso !== null && (
            <p className="flex items-start gap-[9px] text-[13px] leading-relaxed text-texto">
              <Hourglass className="mt-px size-4 shrink-0 text-marca" />
              <span>{textoEnCurso(enCurso, metaHoras)}</span>
            </p>
          )}
          {/* Cerrar la ventana empieza el ayuno desde la última ingesta (Req. 23.14) */}
          {datos.ventana !== null && <CerrarVentana ventana={datos.ventana} />}
          {/*
            Con temporizador, el ayuno lo empieza y lo termina ella (Req. 23.4).
            Se muestra SIEMPRE, también con la ventana abierta: ocultarlo le
            quitaba el método manual, que es justo lo que hay que conservar.
            Quedan las dos salidas: cerrar la ventana cuenta el ayuno desde la
            última ingesta, y "Empezar ayuno ahora" lo cuenta desde este momento
          */}
          {datos.metodo === 'temporizador' && <ControlesDelTemporizador abierto={datos.abierto} hoy={fecha} />}
          {(enCurso !== null || datos.ventana !== null) && <div className="border-t border-borde" />}
          <DetalleDeAyuno estado={estado} />
          <AccionesDeAyuno dia={fecha} estado={estado} />
          {estado.tipo !== 'sin_ayuno' && (
            <Link to="/comida/ayunos" className="inline-flex min-h-11 items-center text-[13px] font-medium text-marca">
              Ver tus ayunos
            </Link>
          )}
        </section>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * Empezó una etapa del plan (Req. 21.8): lo dice con sus metas, también las
 * que no tiene, hasta que se toca "Entendido". Si la meta de kcal depende de
 * un mantenimiento que falta, lo dice y lleva a capturarlo; no inventa cifras.
 */
function AvisoDeEtapa({ aviso, metas }: { aviso: AvisoEtapaNueva; metas: MetasDelDia }) {
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const etapa = NOMBRE_ETAPA[aviso.tipo].toLowerCase();
  const { tiene, sinMeta } = metasEnPalabras(metas);

  async function entendido() {
    setGuardando(true);
    setError(null);
    try {
      await marcarAvisoEtapaVisto(aviso.clave);
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <Banner
      tono="info"
      titulo={
        aviso.empiezaHoy
          ? `Hoy empieza tu etapa de ${etapa}`
          : `Tu etapa de ${etapa} empezó el ${formatearMedio(aviso.inicio)}`
      }
      accion={
        <div className="flex flex-wrap items-center gap-2">
          <Button variante="secundario" completo={false} icono={Check} disabled={guardando} onClick={() => void entendido()}>
            Entendido
          </Button>
          {metas.faltaMantenimiento && (
            <BotonEnlace to="/mas/mantenimiento" variante="fantasma" completo={false}>
              Capturar mantenimiento
            </BotonEnlace>
          )}
          <BotonEnlace to="/mas/plan" variante="fantasma" completo={false}>
            Ver mi plan
          </BotonEnlace>
        </div>
      }
    >
      {tiene.length > 0 && <p>Tus metas: {tiene.join(' · ')}.</p>}
      {metas.faltaMantenimiento && <p>Las calorías salen de tu mantenimiento y todavía no lo capturas.</p>}
      {sinMeta.length > 0 && <p>Sin meta de {lista(sinMeta)} en esta etapa.</p>}
      {error !== null && <p className="text-alerta">{error}</p>}
    </Banner>
  );
}

// ---------------------------------------------------------------------------

interface VistaSesion {
  antetitulo: string;
  pastilla: string | null;
  titulo: string;
  /** "Casa · 4 ejercicios", debajo del nombre. */
  linea: string[];
  /** "Desde las 8:57 a. m." o lo que duró, con el reloj. */
  hora: string | null;
  nota: string;
  accion: string;
  primaria: boolean;
  /** La sesión que propone Gym: el botón la empieza (Req. 24.2); null = solo un enlace. */
  empezar: { routineId: string; modalidad: Modalidad } | null;
  modalidad: Modalidad | null;
  /** Punto verde: hay un entrenamiento abierto ahora. */
  viva: boolean;
}

const ICONO_MODALIDAD: Record<Modalidad, LucideIcon> = { casa: Home, gimnasio: Dumbbell };

/** La próxima sesión que propone Gym (tarea 14): hoy toca, te faltó o sigue. */
function vistaDeProxima(sesion: Extract<SesionDelDia, { tipo: 'proxima' }>): VistaSesion {
  const p = sesion.proxima;
  const empezar = { routineId: p.routineId, modalidad: p.modalidad };
  const base = {
    linea: [ETIQUETAS_MODALIDAD[p.modalidad]],
    hora: null,
    primaria: true,
    empezar,
    modalidad: p.modalidad,
    viva: false,
  };
  const ultima = sesion.ultima === null ? null : `Tu última sesión: ${sesion.ultima.nombre}, ${fechaCorta(sesion.ultima.fecha)}.`;
  if (p.motivo === 'pendiente' && p.fecha !== null) {
    return {
      ...base,
      antetitulo: 'Te faltó',
      pastilla: fechaCorta(p.fecha),
      titulo: p.nombre,
      nota: `Tocaba el ${nombreDia(p.fecha)} ${formatearCorto(p.fecha)}. Puedes retomarla o elegir otra.`,
      accion: 'Retomarla',
    };
  }
  if (p.motivo === 'sigue') {
    return {
      ...base,
      antetitulo: 'Sigue',
      pastilla: null,
      titulo: p.nombre,
      nota:
        p.ultimaDelCiclo === null
          ? 'Es la primera de tus sesiones. Puedes elegir otra.'
          : `La última fue ${p.ultimaDelCiclo.nombre}, ${fechaCorta(p.ultimaDelCiclo.fecha)}. Puedes elegir otra.`,
      accion: 'Empezar',
    };
  }
  return {
    ...base,
    antetitulo: 'Hoy toca',
    pastilla: 'Hoy',
    titulo: p.nombre,
    nota:
      p.tambien !== null
        ? `También te faltó ${p.tambien.nombre} (${fechaCorta(p.tambien.fecha)}): en Gym puedes retomarla.`
        : (ultima ?? 'Todavía no registras sesiones.'),
    accion: 'Empezar',
  };
}

function vistaDeSesion(sesion: SesionDelDia): VistaSesion {
  switch (sesion.tipo) {
    case 'proxima':
      return vistaDeProxima(sesion);
    case 'en_curso':
      return {
        antetitulo: 'Sesión en curso',
        pastilla: 'Hoy',
        titulo: sesion.nombre,
        linea: [ETIQUETAS_MODALIDAD[sesion.modalidad], textoEjercicios(sesion.ejercicios)],
        hora: sesion.desde === '' ? 'En curso' : `Desde las ${sesion.desde}`,
        nota: '',
        accion: 'Continuar entrenamiento',
        primaria: true,
        empezar: null,
        modalidad: sesion.modalidad,
        viva: true,
      };
    case 'hoy':
      return {
        antetitulo: 'Sesión de hoy',
        pastilla: 'Hecha',
        titulo: sesion.nombre,
        linea: [ETIQUETAS_MODALIDAD[sesion.modalidad], textoEjercicios(sesion.ejercicios)],
        hora: sesion.duracion,
        nota: 'Terminaste tu entrenamiento de hoy.',
        accion: 'Ver en Gym',
        primaria: false,
        empezar: null,
        modalidad: sesion.modalidad,
        viva: false,
      };
    case 'ultima':
      return {
        antetitulo: 'Tu última sesión',
        pastilla: fechaCorta(sesion.fecha),
        titulo: sesion.nombre,
        linea: [ETIQUETAS_MODALIDAD[sesion.modalidad], textoEjercicios(sesion.ejercicios)],
        hora: sesion.duracion,
        nota: GYM_DISPONIBLE
          ? 'Hoy todavía no registras entrenamiento.'
          : `Elegir la próxima sesión llega con las rutinas, en la Fase ${FASE_GYM}.`,
        accion: 'Ver en Gym',
        primaria: false,
        empezar: null,
        modalidad: sesion.modalidad,
        viva: false,
      };
    case 'ninguna':
      return {
        antetitulo: 'Tu entrenamiento',
        pastilla: GYM_DISPONIBLE ? null : `Fase ${FASE_GYM}`,
        titulo: 'Sin sesiones todavía.',
        linea: [],
        hora: null,
        nota: GYM_DISPONIBLE
          ? 'Crea una rutina en Gym para empezar.'
          : `Las rutinas y el registro de entrenamientos llegan en la Fase ${FASE_GYM}.`,
        accion: 'Ir a Gym',
        primaria: false,
        empezar: null,
        modalidad: null,
        viva: false,
      };
  }
}

const ICONO_ACTIVIDAD: Record<ActividadDelDia['tipo'], LucideIcon> = {
  en_curso: Timer,
  entreno: Dumbbell,
  sin_registro: CalendarDays,
  pendiente: Clock3,
};

/**
 * La sesión del día en su tarjeta: el estado arriba, el nombre con su
 * emblema, la modalidad y los ejercicios, y la acción principal abajo. La que
 * propone Gym se empieza con un toque (Req. 24.2) o se elige otra en Gym;
 * nada empieza solo.
 *
 * La línea "Actividad de hoy" solo aparece cuando agrega algo: con una sesión
 * en curso o hecha, la tarjeta ya lo dice.
 */
function TarjetaSesion({ sesion, actividad }: { sesion: SesionDelDia; actividad: ActividadDelDia }) {
  const navegar = useNavigate();
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const v = vistaDeSesion(sesion);
  const IconoActividad = ICONO_ACTIVIDAD[actividad.tipo];
  const diceActividad = actividad.tipo === 'sin_registro' || actividad.tipo === 'pendiente';

  async function empezar(routineId: string, modalidad: Modalidad) {
    if (ocupado) return;
    setOcupado(true);
    setError(null);
    try {
      await iniciarEntrenamiento(routineId, new Date(), modalidad);
      void navegar('/gym/entrenamiento');
    } catch (e) {
      setError(mensajeDeError(e));
      setOcupado(false);
    }
  }

  return (
    <section aria-labelledby="titulo-sesion" className="rounded-panel bg-superficie p-5">
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          {v.viva && <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-marca" />}
          <span className="antetitulo truncate">{v.antetitulo}</span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {v.modalidad !== null && (
            <>
              {(() => {
                const IconoModalidad = ICONO_MODALIDAD[v.modalidad];
                return <IconoModalidad aria-hidden="true" className="size-4 text-texto-suave" />;
              })()}
              <span className="sr-only">{ETIQUETAS_MODALIDAD[v.modalidad]}</span>
            </>
          )}
          {v.pastilla !== null && <Pastilla>{v.pastilla}</Pastilla>}
        </span>
      </div>

      <div className="mt-3 flex items-start gap-3.5">
        <Emblema icono={Dumbbell} />
        <div className="min-w-0 flex-1">
          <h2 id="titulo-sesion" className="break-words text-[22px] leading-tight tracking-[-0.6px] text-texto">
            {v.titulo}
          </h2>
          {v.linea.length > 0 && (
            <p className="mt-1 text-[13px] text-texto-suave">{v.linea.join(' · ')}</p>
          )}
          {v.hora !== null && (
            <p className="mt-1 flex items-center gap-[7px] text-[12px] text-texto-suave">
              <Clock3 aria-hidden="true" className="size-3.5" />
              {v.hora}
            </p>
          )}
        </div>
      </div>

      {v.nota !== '' && <p className="mt-2.5 text-[12px] leading-relaxed text-texto-suave">{v.nota}</p>}
      {diceActividad && (
        <p className="mt-2.5 flex items-center gap-[9px] text-[12px] text-texto-suave">
          <IconoActividad aria-hidden="true" className="size-4 text-marca" />
          <span>Actividad de hoy · {actividad.texto}</span>
        </p>
      )}

      <div className="mt-4 grid gap-2">
        {v.empezar !== null ? (
          <>
            <Button
              icono={ArrowRight}
              tamano="grande"
              disabled={ocupado}
              onClick={() => v.empezar !== null && void empezar(v.empezar.routineId, v.empezar.modalidad)}
            >
              {ocupado ? 'Empezando…' : v.accion}
            </Button>
            <BotonEnlace to="/gym" variante="secundario">
              Elegir otra en Gym
            </BotonEnlace>
            {error !== null && (
              <Banner tono="alerta" titulo="No se pudo empezar">
                {error}
              </Banner>
            )}
          </>
        ) : (
          <BotonEnlace
            to={v.primaria ? '/gym/entrenamiento' : '/gym'}
            variante={v.primaria ? 'primario' : 'secundario'}
            icono={ArrowRight}
            {...(v.primaria ? { tamano: 'grande' as const } : {})}
          >
            {v.accion}
          </BotonEnlace>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

/** Desayuno, comida y cena con lo registrado hoy, y las colaciones si hay. */
function TarjetaComidas({ fecha, comidas }: { fecha: FechaLocal; comidas: ResumenComidas }) {
  const colaciones = textoColaciones(comidas.colaciones);
  return (
    <Tarjeta
      titulo="Tus comidas"
      extra={
        <span className="whitespace-nowrap text-[12px] text-texto-suave">
          {comidas.registrados} de {comidas.total} registradas
        </span>
      }
    >
      {/* El signo más lleva a registrar esa comida; la palomita, a lo registrado. */}
      <ListaComidas
        comidas={comidas}
        enlaceDe={(t) =>
          t.registros > 0 ? `/comida?tiempo=${t.tiempo}` : `/comida/agregar?fecha=${fecha}&tiempo=${t.tiempo}`
        }
      />
      {colaciones !== null && <p className="pt-2 text-[12px] text-texto-suave">Además: {colaciones}</p>}
    </Tarjeta>
  );
}

// ---------------------------------------------------------------------------

/**
 * El estado del respaldo (Req. 7.3). Ya no se invita a completar el perfil:
 * un dato para un cálculo se pide en el cálculo que lo usa (Req. 4.5).
 */
function Pendientes({ settings, hayDatos }: { settings: Settings; hayDatos: boolean }) {
  const respaldo = evaluarRespaldo({
    ultimoRespaldoEn: settings.ultimoRespaldoEn,
    hayDatos,
    diasAviso: settings.diasAvisoRespaldo,
  });

  const detalleRespaldo = !hayDatos
    ? 'Aún no hay datos que respaldar.'
    : respaldo.debeAvisar
      ? (respaldo.detalle ?? 'Conviene hacer un respaldo.')
      : 'Conviene respaldar cada semana.';

  return (
    <Filas tarjetas>
      <Fila
        tarjeta
        icono={Archive}
        titulo={`Último respaldo: ${textoUltimoRespaldo(settings.ultimoRespaldoEn)}`}
        detalle={detalleRespaldo}
        a="/mas/respaldos"
      />
    </Filas>
  );
}
